import React, { ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import './index.less';

import MessageInput from '../MessageInput';
import MessageListItem, { IMessageListItem } from '../MessageListItem';
import IbTypography from '../common/IbTypography';
import IbButton from '../common/IbButton';
import IbIcon from '../common/IbIcon';
import AuthCard from '../AuthCard';
import {
  AgentRunModel,
  ChatMessageContentInner,
  DataInterfaceInput,
  DataInterfaceType,
  DataPayload,
  SessionModel,
  SessionProviderModel,
  SessionStatus,
} from '../../../gamma-api';
import RunRetryButton from '../RunRetryButton';

const MAIN_CLASS_NAME = 'message-list';
const CHAT_BUTTONS_CLASS_NAME = `${MAIN_CLASS_NAME}__chat-buttons`;
const BUTTON_CLASS_NAME = `${CHAT_BUTTONS_CLASS_NAME}__button`;
const CONTENT_CLASS_NAME = `${MAIN_CLASS_NAME}__content`;
const EMPTY_CONTENT_CLASS_NAME = `${CONTENT_CLASS_NAME}_empty`;
const SCROLL_CLASS_NAME = `${CONTENT_CLASS_NAME}__scroll`;
const MESSAGE_PROCESSING_CLASS_NAME = `${SCROLL_CLASS_NAME}__message-processing`;
const FOOTER_CLASS_NAME = `${MAIN_CLASS_NAME}__footer`;

export interface IMessageListProps {
  readonly?: boolean;
  loading?: boolean;
  agentId?: string;
  authSession?: SessionModel | null;
  messageProcessing?: boolean;
  messageList: IMessageListItem[];
  dataInterface?: DataInterfaceInput;
  codeFolder?: string;
  inputEditorPrefix?: ReactNode;
  inputEditorSuffix?: ReactNode;
  onMessageSelect?: (message: IMessageListItem) => void;
  onMessageSend?: (content: ChatMessageContentInner, payload: DataPayload) => void;
  onRunRetry?: (run: AgentRunModel) => void;
  onSignIn?: (provider: SessionProviderModel) => void;
  onSignInCancel?: (provider: SessionProviderModel) => void;
  onSignOut?: () => void;
  onClean?: () => void;
}

const MessageList: React.FC<IMessageListProps> = ({
  readonly,
  loading,
  agentId,
  authSession,
  messageProcessing,
  messageList,
  dataInterface,
  codeFolder,
  inputEditorPrefix,
  inputEditorSuffix,
  onMessageSelect,
  onMessageSend,
  onRunRetry,
  onSignIn,
  onSignInCancel,
  onSignOut,
  onClean,
}) => {
  const { t } = useTranslation();

  const messagesListEndRef = useRef<HTMLDivElement>(null);
  const messageListIsEmpty = !messageList.length;
  const messageSendingEnabled = !loading && !messageProcessing;

  const renderMessageItem = (message: IMessageListItem) => (
    <MessageListItem key={message.id} message={message} onMessageSelect={onMessageSelect} />
  );

  const onMessageListLengthAndLoadingChange = () => {
    messagesListEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  useEffect(onMessageListLengthAndLoadingChange, [messageList.length, loading]);

  const contentClasses = [CONTENT_CLASS_NAME];
  if (messageListIsEmpty) {
    contentClasses.push(EMPTY_CONTENT_CLASS_NAME);
  }

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={contentClasses.join(' ')}>
        {messageListIsEmpty ? (
          <IbTypography.Paragraph disabled type="secondary">
            {t('Chat: Ask the bot a question to start a conversation')}
          </IbTypography.Paragraph>
        ) : (
          <div className={SCROLL_CLASS_NAME}>
            {messageList.map(renderMessageItem)}
            {messageProcessing ? (
              <div className={MESSAGE_PROCESSING_CLASS_NAME}>
                <div className="dot-loader" />
                <IbTypography.Paragraph disabled type="secondary">
                  {t("Chat: I'm already thinking about an answer, please wait...")}
                </IbTypography.Paragraph>
              </div>
            ) : null}
            <div ref={messagesListEndRef} />
          </div>
        )}
      </div>
      {!readonly && (
        <>
          <div className={FOOTER_CLASS_NAME}>
            {!authSession || authSession.status === SessionStatus.SignedIn ? (
              <MessageInput
                codeFolder={codeFolder}
                dataInterface={dataInterface}
                editorPrefix={inputEditorPrefix}
                editorSuffix={inputEditorSuffix}
                messageSendingEnabled={messageSendingEnabled}
                onMessageSend={onMessageSend}
              />
            ) : (
              authSession.providers.map(
                (provider, index) =>
                  provider.status === SessionStatus.Challenged && (
                    <AuthCard key={index} provider={provider} onCancel={onSignInCancel} onSignIn={onSignIn} />
                  )
              )
            )}
          </div>
          <div className={CHAT_BUTTONS_CLASS_NAME}>
            {dataInterface?.type === DataInterfaceType.Api && !!agentId && (
              <RunRetryButton agentId={agentId} dataInterface={dataInterface} onRetry={onRunRetry} />
            )}
            {messageListIsEmpty
              ? null
              : (!authSession || authSession.status === SessionStatus.SignedIn) && (
                  <IbButton
                    className={BUTTON_CLASS_NAME}
                    icon={<IbIcon iconName="refresh" />}
                    type="link"
                    onClick={onClean}
                  >
                    {t('Chat: Clean chat')}
                  </IbButton>
                )}
            {authSession?.status === SessionStatus.SignedIn && (
              <IbButton
                className={BUTTON_CLASS_NAME}
                disabled={messageProcessing}
                icon={<IbIcon iconName="logout" />}
                type="icon"
                onClick={onSignOut}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MessageList;
